import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { useCallAnalytics } from "@/hooks/useCallAnalytics";
import { Activity, Phone, Clock, Target } from "lucide-react";

interface AnalyticsCardsProps {
  timeRange?: '7d' | '30d' | '90d';
}

export const AnalyticsCards = ({ timeRange = '30d' }: AnalyticsCardsProps) => {
  const { data: analytics, isLoading } = useCallAnalytics(timeRange);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSentimentDisplay = (sentiment: number | null) => {
    if (sentiment === null) return { value: 'N/A', color: 'text-muted-foreground' };
    const percentage = Math.round((sentiment + 1) * 50);
    if (sentiment > 0.2) return { value: `${percentage}% Positive`, color: 'text-green-600' };
    if (sentiment < -0.2) return { value: `${percentage}% Negative`, color: 'text-red-600' };
    return { value: `${percentage}% Neutral`, color: 'text-yellow-600' };
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <div className="h-7 sm:h-8 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const sentiment = getSentimentDisplay(analytics?.avgSentiment || null);

  const cards = [
    {
      title: "Total Calls",
      icon: Phone,
      value: analytics?.totalCalls || 0,
      subtitle: `${analytics?.totalAnsweredCalls || 0} answered`,
      delay: 0.1
    },
    {
      title: "Answer Rate",
      icon: Target,
      value: analytics?.answerRate ? `${Math.round(analytics.answerRate)}%` : '0%',
      subtitle: "of total calls answered",
      delay: 0.2
    },
    {
      title: "Avg Duration",
      icon: Clock,
      value: analytics?.avgDuration ? formatDuration(analytics.avgDuration) : '0:00',
      subtitle: "for completed calls",
      delay: 0.3
    },
    {
      title: "Sentiment",
      icon: Activity,
      value: sentiment.value,
      valueClass: sentiment.color,
      subtitle: "average call sentiment",
      delay: 0.4
    }
  ];

  return (
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <motion.div 
          key={card.title}
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: card.delay }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-4 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">{card.title}</CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              <div className={`text-xl sm:text-2xl font-bold ${card.valueClass || ''}`}>
                {card.value}
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block">
                {card.subtitle}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
};
